import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
    Card, 
    Spinner, 
    Alert, 
    Badge,
    Row,
    Col,
    Container,
    Button
} from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import BASE_URL from "../config";
import axios from 'axios';
import { 
    FaMapMarkerAlt, 
    FaMapPin, 
    FaCar, 
    FaRegClock,
    FaRoute,
    FaPhone,
    FaBox,
    FaTruck,
    FaExclamationTriangle
} from "react-icons/fa";

const authAxios = axios.create({ baseURL: BASE_URL });
authAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem("authToken");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Fix for Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom icons for different markers
const createIcon = (color, icon = null) => {
    return L.divIcon({
        className: 'custom-div-icon',
        html: `<div style="background-color: ${color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-size: 14px; font-weight: bold;">${icon || ''}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15]
    });
};

const pickupIcon = createIcon('#28a745', '📍'); // Green with pin icon
const dropIcon = createIcon('#dc3545', '🏁');   // Red with flag icon
const driverIcon = createIcon('#007bff', '🚗'); // Blue with car icon

// Component to fit map bounds
const FitBounds = ({ bounds }) => {
    const map = useMap();
    
    useEffect(() => {
        if (bounds && bounds.length > 0) {
            const leafletBounds = L.latLngBounds(bounds);
            map.fitBounds(leafletBounds, { padding: [50, 50], maxZoom: 16 });
        }
    }, [bounds, map]);
    
    return null;
};

// Function to reverse geocode coordinates to address
const geocodeLatLng = async (lat, lng) => {
    try {
        const res = await axios.get(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`
        );
        return res.data.display_name || 'Address not found';
    } catch (err) {
        console.error('Geocoding failed:', err);
        return 'Geocoding failed';
    }
};

// Function to calculate estimated time of arrival
const calculateETA = (distance, speed = 40) => {
    // Default speed: 40 km/h for city driving
    const timeInHours = distance / speed;
    const hours = Math.floor(timeInHours);
    const minutes = Math.round((timeInHours - hours) * 60);
    
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

// Function to calculate distance between two points in km
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const d = R * c; // Distance in km
    return d;
};

const deg2rad = (deg) => {
    return deg * (Math.PI/180);
};

const PublicTracking = () => {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();
    const driverId = searchParams.get('driverId');
    
    const [order, setOrder] = useState(null);
    const [currentDriverLocation, setCurrentDriverLocation] = useState(null);
    const [driverAddress, setDriverAddress] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [distance, setDistance] = useState(null);
    const [eta, setEta] = useState(null);
    
    // Default center (Dubai)
    const defaultCenter = [25.2048, 55.2708];
    const mapStyle = { height: '400px', width: '100%' };
    
    // Public API endpoint
    const DRIVER_LOCATIONS_API = `${BASE_URL}/driver-locations`;;
console.log("BASE_URL =", BASE_URL);
console.log("Request URL =", `${DRIVER_LOCATIONS_API}?driver_id=${driverId}`);

    // Function to fetch order details from URL parameters
    const fetchOrderDetails = () => {
        try {
            // Get order details from URL parameters
            const po = searchParams.get('po');
            const pickup = searchParams.get('pickup');
            const drop = searchParams.get('drop');
            const driver = searchParams.get('driver');
            const pickupLat = searchParams.get('pickupLat');
            const pickupLng = searchParams.get('pickupLng');
            const dropLat = searchParams.get('dropLat');
            const dropLng = searchParams.get('dropLng');
            
            const orderData = {
                id: orderId,
                po_number: po || `Order ${orderId}`,
                status: 'in_transit',
                type: 'purchase_order',
                pickup_address: pickup || 'Pickup Location',
                delivery_address: drop || 'Delivery Location',
                pickup_lat: pickupLat || 25.2048,
                pickup_lng: pickupLng || 55.2708,
                delivery_lat: dropLat || 25.0748,
                delivery_lng: dropLng || 55.1908,
                driver_name: driver || 'Driver',
                driver_phone: '+971XXXXXXXXX'
            };
            
            setOrder(orderData);
        } catch (err) {
            console.error("Error fetching order details:", err);
            setError("Failed to fetch order details");
        }
    };

    // Function to fetch driver location using public API
    const fetchDriverLocation = async () => {
        if (!driverId) return;
        
        try {
                console.log("BASE_URL =", BASE_URL);
                console.log("Request URL =", `${DRIVER_LOCATIONS_API}?driver_id=${driverId}`);
            const res = await axios.get(`${DRIVER_LOCATIONS_API}?driver_id=${driverId}`);
            const driversData = res.data?.data?.drivers;
            console.log("Driver data:", res.data);
            if (Array.isArray(driversData)) {
                const driver = driversData.find(d => d.id === parseInt(driverId));
                if (driver && driver.location) {
                    const lat = parseFloat(driver.location.lat);
                    const lng = parseFloat(driver.location.lng);
                    setCurrentDriverLocation({
                        lat,
                        lng,
                        timestamp: driver.location.timestamp
                    });
                    
                    const address = await geocodeLatLng(lat, lng);
                    setDriverAddress(address);
                    setLastUpdated(new Date());
                    
                    // Calculate distance and ETA if we have drop location
                    if (order?.delivery_lat && order?.delivery_lng) {
                        const dropLat = parseFloat(order.delivery_lat);
                        const dropLng = parseFloat(order.delivery_lng);
                        const dist = calculateDistance(lat, lng, dropLat, dropLng);
                        setDistance(dist);
                        setEta(calculateETA(dist));
                    }
                } else {
                    console.warn("No location data found for driver:", driverId);
                    setCurrentDriverLocation(null);
                    setDriverAddress("");
                    setDistance(null);
                    setEta(null);
                }
            } else {
                console.warn("Invalid driver locations data format");
                setCurrentDriverLocation(null);
                setDriverAddress("");
                setDistance(null);
                setEta(null);
            }
        } catch (err) {
            console.error("Error fetching driver location:", err);
            setError("Failed to fetch driver location");
            setCurrentDriverLocation(null);
            setDriverAddress("");
            setDistance(null);
            setEta(null);
        }
    };

    // Initial data fetch
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            fetchOrderDetails();
            setLoading(false);
        };
        
        fetchData();
    }, [orderId]);

    // Set up interval for real-time tracking
    useEffect(() => {
        if (order && driverId) {
            fetchDriverLocation();
            const intervalId = setInterval(fetchDriverLocation, 30000); // Update every 30 seconds
            
            return () => clearInterval(intervalId);
        }
    }, [order, driverId]);

    // Get all marker positions for bounds calculation
    const getMarkerPositions = () => {
        const positions = [];
        
        if (order?.pickup_lat && order?.pickup_lng) {
            positions.push([parseFloat(order.pickup_lat), parseFloat(order.pickup_lng)]);
        }
        
        if (order?.delivery_lat && order?.delivery_lng) {
            positions.push([parseFloat(order.delivery_lat), parseFloat(order.delivery_lng)]);
        }
        
        if (currentDriverLocation) {
            positions.push([currentDriverLocation.lat, currentDriverLocation.lng]);
        }
        
        return positions;
    };

    // Get route coordinates for polyline
    const getRouteCoordinates = () => {
        if (!order) return [];
        
        const route = [];
        
        if (order.pickup_lat && order.pickup_lng) {
            route.push([parseFloat(order.pickup_lat), parseFloat(order.pickup_lng)]);
        }
        
        if (currentDriverLocation) {
            route.push([currentDriverLocation.lat, currentDriverLocation.lng]);
        }
        
        if (order.delivery_lat && order.delivery_lng) {
            route.push([parseFloat(order.delivery_lat), parseFloat(order.delivery_lng)]);
        }
        
        return route;
    };

    const markerPositions = getMarkerPositions();
    const routeCoordinates = getRouteCoordinates();

    if (loading) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <Spinner animation="border" />
                    <div className="mt-3">Loading tracking information...</div>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    <FaExclamationTriangle className="me-2" />
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <Card className="mb-4">
                <Card.Header className="bg-primary text-white">
                    <h3 className="mb-0"><FaTruck className="me-2" /> Order Tracking</h3>
                </Card.Header>
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <h5>Order #{order?.po_number || order?.id}</h5>
                            <p><strong>Status:</strong> <Badge bg="info">{order?.status}</Badge></p>
                            <p><strong>Order Type:</strong> {order?.type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                        </Col>
                        <Col md={6}>
                            <p><strong>Driver:</strong> {order?.driver_name}</p>
                            {order?.driver_phone && (
                                <p><strong>Driver Phone:</strong> <FaPhone className="me-1" /> {order.driver_phone}</p>
                            )}
                            {lastUpdated && (
                                <p><strong>Last Updated:</strong> <FaRegClock className="me-1" /> {lastUpdated.toLocaleString()}</p>
                            )}
                        </Col>
                    </Row>
                    
                    <Row className="mb-3">
                        <Col md={4}>
                            <Card className="h-100 border-success">
                                <Card.Body>
                                    <Card.Title className="text-success"><FaMapPin className="me-2" /> Pickup</Card.Title>
                                    <Card.Text>
                                        <small>{order?.pickup_address || 'N/A'}</small>
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="h-100 border-danger">
                                <Card.Body>
                                    <Card.Title className="text-danger"><FaMapPin className="me-2" /> Drop</Card.Title>
                                    <Card.Text>
                                        <small>{order?.delivery_address || 'N/A'}</small>
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="h-100 border-primary">
                                <Card.Body>
                                    <Card.Title className="text-primary"><FaCar className="me-2" /> Driver</Card.Title>
                                    <Card.Text>
                                        <small>{order?.driver_name}</small><br />
                                        {driverAddress && (
                                            <small className="text-muted">{driverAddress}</small>
                                        )}
                                    </Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    
                    {distance && eta && (
                        <Row className="mb-3">
                            <Col md={6}>
                                <Card className="border-info">
                                    <Card.Body className="text-center">
                                        <Card.Title className="text-info"><FaRoute className="me-2" /> Distance</Card.Title>
                                        <Card.Text>
                                            <span className="fs-4">{distance.toFixed(1)} km</span>
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={6}>
                                <Card className="border-warning">
                                    <Card.Body className="text-center">
                                        <Card.Title className="text-warning"><FaRegClock className="me-2" /> ETA</Card.Title>
                                        <Card.Text>
                                            <span className="fs-4">{eta}</span>
                                        </Card.Text>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    
                    <div style={mapStyle}>
                        <MapContainer 
                            center={markerPositions.length > 0 ? markerPositions[0] : defaultCenter}
                            zoom={markerPositions.length > 0 ? 13 : 5}
                            style={mapStyle}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                language="en"
                                detectRetina={true}
                            />
                            
                            {markerPositions.length > 0 && <FitBounds bounds={markerPositions} />}
                            
                            {order?.pickup_lat && order?.pickup_lng && (
                                <Marker 
                                    position={[parseFloat(order.pickup_lat), parseFloat(order.pickup_lng)]} 
                                    icon={pickupIcon}
                                >
                                    <Popup>
                                        <strong>Pickup</strong><br />
                                        <small>{order.pickup_address}</small>
                                    </Popup>
                                </Marker>
                            )}
                            
                            {order?.delivery_lat && order?.delivery_lng && (
                                <Marker 
                                    position={[parseFloat(order.delivery_lat), parseFloat(order.delivery_lng)]} 
                                    icon={dropIcon}
                                >
                                    <Popup>
                                        <strong>Drop</strong><br />
                                        <small>{order.delivery_address}</small>
                                    </Popup>
                                </Marker>
                            )}
                            
                            {currentDriverLocation && (
                                <Marker 
                                    position={[currentDriverLocation.lat, currentDriverLocation.lng]} 
                                    icon={driverIcon}
                                >
                                    <Popup>
                                        <strong>Driver Location</strong><br />
                                        <small>{driverAddress || 'Address not available'}</small><br />
                                        <small>Last updated: {new Date(currentDriverLocation.timestamp).toLocaleString()}</small>
                                    </Popup>
                                </Marker>
                            )}
                            
                            {routeCoordinates.length > 1 && (
                                <Polyline 
                                    positions={routeCoordinates} 
                                    color="#007bff" 
                                    weight={4} 
                                    opacity={0.7}
                                />
                            )}
                        </MapContainer>
                    </div>
                    
                    <div className="mt-3 d-flex justify-content-center">
                        <div className="d-flex align-items-center me-4">
                            <div style={{ width: '20px', height: '20px', backgroundColor: '#28a745', borderRadius: '50%', marginRight: '8px' }}></div>
                            <span>Pickup</span>
                        </div>
                        <div className="d-flex align-items-center me-4">
                            <div style={{ width: '20px', height: '20px', backgroundColor: '#dc3545', borderRadius: '50%', marginRight: '8px' }}></div>
                            <span>Drop</span>
                        </div>
                        <div className="d-flex align-items-center">
                            <div style={{ width: '20px', height: '20px', backgroundColor: '#007bff', borderRadius: '50%', marginRight: '8px' }}></div>
                            <span>Driver</span>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default PublicTracking;